import Typography from '@mui/material/Typography';
import { getSocialMediaInfo, updateSocialMediaInfo, deleteSocialMediaInfo } from "../../services/socialMediaPlatformInformation";
import { SocialMediaPlatformInfo, newSocialMediaPlatform } from "../../data/socialMediaData";
import { DataGrid, GridActionsCellItem, GridCellParams, GridRowId } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import { randomId } from '@mui/x-data-grid-generator';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconList from "../../data/account/socialMediaIcons";
import "./SocialMediaPlatforms.css";



export default function SocialMediaPlatform() {
    const [socialMediaPlatformInfo, setSocialMediaPlatformInfo] = useState<SocialMediaPlatformInfo[]>(newSocialMediaPlatform);
    const [editRowsModel, setEditRowsModel] = useState<Record<GridRowId, boolean>>({});
    const [tempRow, setTempRow] = useState<SocialMediaPlatformInfo | null>(null);
    const [editingRowId, setEditingRowId] = useState<GridRowId | null>(null);
    const [editMode, setEditMode] = useState(false);

    // Function to process row updates
    const processRowUpdate = (newRow: any, oldRow: any) => {
        const updatedRows = socialMediaPlatformInfo.map((row) => (
            row.Id === newRow.Id ? { ...row, [newRow.field]: newRow[newRow.field] } : row
        ));

        const rowToUpdate = updatedRows.find(row => row.Id === newRow.Id);
        if (!rowToUpdate) {
            console.error(`Row with ID ${newRow.Id} not found.`);
            return oldRow;
        }

        setSocialMediaPlatformInfo(updatedRows);
        return rowToUpdate;
    };

    // Hook to generate a unique ID
    const generateId = useCallback(() => randomId(), []);

    // Effect to fetch social media information on component mount
    useEffect(() => {
        const fetchSocialMediaInfo = async () => {
            try {
                const smInfo = await getSocialMediaInfo();
                setSocialMediaPlatformInfo(smInfo);
                const id = generateId();
                setTempRow({ Id: id, Name: '', Icon: '' });
            } catch (error) {
                console.error('Error fetching social media info:', error);
            }
        };

        fetchSocialMediaInfo();
    }, [generateId]);

    const handleEdit = useCallback((id: GridRowId) => {
        setEditRowsModel(prev => ({ ...prev, [id]: true }));
    }, []);

    const handleSave = async (id: GridRowId, newRow: any) => {
        let updatedRows;
        let inputIsEmpty = !newRow.Name.trim(); // Check if the Name field is empty or contains only whitespace

        if (tempRow && id === tempRow.Id) {
            if (inputIsEmpty) {
                console.log("Cannot save an empty row.");
                return; 
            }
            
            updatedRows = [...socialMediaPlatformInfo, { ...tempRow, ...newRow }];
            setTempRow(null); 
        } else {
            updatedRows = socialMediaPlatformInfo.map(row => row.Id === id ? { ...row, ...newRow } : row);
        }

        // Perform the update operation (e.g., updating the backend)
        await updateSocialMediaInfo(updatedRows);

        // Update the state with the new list of rows
        setSocialMediaPlatformInfo(updatedRows);

        // Reset the editing model as the save operation is complete
        setEditRowsModel(prev => {
            const newModel = { ...prev };
            delete newModel[id];
            return newModel;
        });
        setEditingRowId(null);
        // After saving, if the input was not empty, add a new temporary row for further inputs
        if (!inputIsEmpty) {
            const newId = generateId();
            const newTempRow = { Id: newId, Name: '', Icon: '' }; 
            setTempRow(newTempRow); 
            setEditRowsModel(prev => ({ ...prev, [newId]: true }));
        }
    };

    const handleCancel = (id: GridRowId) => {
        if (tempRow && id === tempRow.Id) {
            // If cancelling the temporary row, simply reset the edit state without removing the row
            setEditRowsModel(prev => {
                const newModel = { ...prev };
                delete newModel[id];
                return newModel;
            });
        } else {
            // For existing rows, revert changes by resetting to the previous state
            setSocialMediaPlatformInfo(prevInfo => prevInfo.map(row => row.Id === id ? socialMediaPlatformInfo.find(r => r.Id === id) ?? row : row));
            setEditRowsModel(prev => {
                const newModel = { ...prev };
                delete newModel[id];
                return newModel;
            });
        }
        setEditingRowId(null);
    };

    const handleDelete = async (id: GridRowId) => {
        const deletedRow = socialMediaPlatformInfo.find(row => row.Id === id);
        if (!deletedRow) {
            console.error('Row not found for deletion:', id);
            return;
        }
        const updatedRows = socialMediaPlatformInfo.filter(row => row.Id !== id);
        await deleteSocialMediaInfo(deletedRow);
        setSocialMediaPlatformInfo(updatedRows);
    };
    // Define columns for the DataGrid
    const columns = [
        { field: 'Name', headerName: 'Name', width: 150, editable: editMode },
        { field: 'Icon', headerName: 'Icon', width: 150, editable: editMode },
        {
            field: '', headerName: '', width: 100, editable: false,
            renderCell: (params: GridCellParams) => {
                const Icon = IconList[params.row.Name.toLowerCase()];
                return Icon ? <Icon /> : null;
            }
        },
        {
            field: 'actions', headerName: 'Actions', width: 150, editable: false,
            renderCell: (params: GridCellParams) => renderActionCell(params)
        },
    ];
    

    // Render function for action cell
    const renderActionCell = (params: GridCellParams) => {
        const row = params.row as SocialMediaPlatformInfo;
        const isInEditMode = editRowsModel[row.Id];
        setEditMode(isInEditMode);

        return isInEditMode ? [
            <GridActionsCellItem key="save" icon={<SaveIcon />} label="Save" onClick={() => handleSave(row.Id, row)} />,
            <GridActionsCellItem key="cancel" icon={<CancelIcon />} label="Cancel" onClick={() => handleCancel(row.Id)} />,
        ] : [
            <GridActionsCellItem key="edit" icon={<EditIcon />} label="Edit" onClick={() => handleEdit(row.Id)} />,
            <GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" onClick={() => handleDelete(row.Id)} />,
        ];
    };

    return (
        <div className="form-data">
            <Typography id="modal-modal-title" variant="h4" component="h2" align="left">
                Social Media Platforms
            </Typography>
            <div className='typo'>
                <DataGrid
                    rows={tempRow ? [...socialMediaPlatformInfo, tempRow] : socialMediaPlatformInfo}
                    columns={columns}
                    getRowId={(row) => row.Id}
                    onRowEditStop={processRowUpdate}
                    scrollbarSize={10} 
                    getRowClassName={(params) => {
                        if (params.id === tempRow?.Id) {
                            return editRowsModel[params.id] ? 'editingRow' : 'newRow'; // Apply different style if in edit mode
                        }
                        return editRowsModel[params.id] ? 'editingRow' : ''; // Apply default style for other editing rows
                    }}
                />
            </div>
        </div>
    );
}